<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';

const props = defineProps({
  activity: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['complete', 'edit', 'delete']);

const { t } = useI18n();

const ACTIVITY_TYPE_ICONS = {
  call: 'i-lucide-phone',
  email: 'i-lucide-mail',
  meeting: 'i-lucide-users',
  visit: 'i-lucide-map-pin',
  other: 'i-lucide-more-horizontal',
};

const typeIcon = computed(
  () =>
    ACTIVITY_TYPE_ICONS[props.activity.activityType] ||
    ACTIVITY_TYPE_ICONS.other
);

const isCompleted = computed(() => props.activity.status === 'completed');

const statusLabel = computed(() =>
  t(`ACTIVITIES.STATUS.${props.activity.status?.toUpperCase()}`)
);

const statusClass = computed(() => {
  const map = {
    pending: 'bg-amber-50 text-amber-700',
    completed: 'bg-green-50 text-green-700',
    snoozed: 'bg-orange-50 text-orange-700',
  };
  return map[props.activity.status] || map.pending;
});

const dueDate = computed(() => {
  if (!props.activity.dueDate) return null;
  return new Date(props.activity.dueDate * 1000);
});

const formattedDueDate = computed(() => {
  if (!dueDate.value) return '';
  return dueDate.value.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});

const priority = computed(() => {
  if (isCompleted.value || !dueDate.value) return 'low';
  const now = new Date();
  const startToday = new Date(now);
  startToday.setHours(0, 0, 0, 0);
  const endToday = new Date(now);
  endToday.setHours(23, 59, 59, 999);
  const endWeek = new Date(startToday);
  endWeek.setDate(endWeek.getDate() + 7);
  if (dueDate.value < startToday) return 'urgent';
  if (dueDate.value <= endToday) return 'high';
  if (dueDate.value <= endWeek) return 'medium';
  return 'low';
});

const priorityLabel = computed(() =>
  t(`ACTIVITIES.PRIORITY.${priority.value.toUpperCase()}`)
);

const priorityClass = computed(() => {
  const map = {
    urgent: 'bg-red-50 text-red-700',
    high: 'bg-orange-50 text-orange-700',
    medium: 'bg-yellow-50 text-yellow-700',
    low: 'bg-slate-100 text-slate-600',
  };
  return map[priority.value] || map.low;
});

const contactName = computed(() => props.activity.contact?.name || '');
const assigneeName = computed(() => props.activity.assignee?.name || '');
const description = computed(() => props.activity.description || '');

const handleComplete = () => emit('complete', props.activity.id);
const handleEdit = () => emit('edit', props.activity);
const handleDelete = () => emit('delete', props.activity);
</script>

<template>
  <div
    class="flex items-start gap-3 p-4 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 shadow-sm rounded-lg transition-all hover:shadow-md hover:border-gray-200 dark:hover:border-slate-600"
  >
    <div
      class="flex items-center justify-center size-9 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 shrink-0"
    >
      <span :class="typeIcon" class="size-4" />
    </div>

    <div class="flex-1 min-w-0 flex flex-col gap-1.5">
      <div class="flex items-center gap-2 flex-wrap">
        <span
          class="text-sm font-medium text-slate-900 dark:text-slate-100 truncate"
        >
          {{ activity.title }}
        </span>
        <span
          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
          :class="statusClass"
        >
          {{ statusLabel }}
        </span>
        <span
          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
          :class="priorityClass"
        >
          {{ priorityLabel }}
        </span>
      </div>

      <p
        v-if="description"
        class="text-sm text-slate-500 dark:text-slate-400 line-clamp-2"
      >
        {{ description }}
      </p>

      <div
        class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400"
      >
        <span v-if="formattedDueDate" class="inline-flex items-center gap-1">
          <span class="i-lucide-calendar size-3.5" />
          {{ formattedDueDate }}
        </span>
        <span v-if="contactName" class="inline-flex items-center gap-1">
          <span class="i-lucide-user size-3.5" />
          {{ contactName }}
        </span>
        <span v-if="assigneeName" class="inline-flex items-center gap-1">
          <span class="i-lucide-user-check size-3.5" />
          {{ assigneeName }}
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-1 self-center shrink-0">
      <Button
        v-if="!isCompleted"
        variant="faded"
        color="blue"
        size="xs"
        icon="i-lucide-check"
        @click="handleComplete"
      />
      <Button
        variant="ghost"
        color="slate"
        size="xs"
        icon="i-lucide-pencil"
        @click="handleEdit"
      />
      <ConfirmButton
        variant="ghost"
        color="slate"
        size="xs"
        icon="i-lucide-trash-2"
        :confirm-label="t('ACTIVITIES.CARD.CONFIRM_DELETE')"
        confirm-color="ruby"
        @click="handleDelete"
      />
    </div>
  </div>
</template>
